import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { AgingBuckets } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { formatAxis, plnTooltipFormatter } from './chart-utils';

interface AgingChartProps {
  receivables: AgingBuckets;
  payables: AgingBuckets;
  showReceivables: boolean;
}

const BUCKET_KEYS = ['current', 'd30', 'd60', 'd90', 'older'] as const;

/**
 * Single-hue sequential ramp derived from the indigo chart token: mixing with
 * the card surface keeps steps ordered light→dark (= older) in light mode and
 * surface→vivid in dark mode, so the ramp stays token-driven and theme-safe.
 * Mixed in sRGB, not oklch — the card is achromatic and oklch interpolation
 * in Chrome drags the hue toward the white's hue angle (pink), while sRGB
 * mixing with white/black always preserves the indigo hue family.
 * The 90+ bucket keeps the darkest/strongest step but carries a subtle
 * destructive tint to flag seriously overdue amounts without breaking the
 * sequential logic.
 */
const BUCKET_COLORS: Record<(typeof BUCKET_KEYS)[number], string> = {
  current: 'color-mix(in srgb, var(--chart-1) 25%, var(--card))',
  d30: 'color-mix(in srgb, var(--chart-1) 45%, var(--card))',
  d60: 'color-mix(in srgb, var(--chart-1) 65%, var(--card))',
  d90: 'color-mix(in srgb, var(--chart-1) 85%, var(--card))',
  older: 'color-mix(in srgb, var(--chart-1) 78%, var(--destructive))',
};

function total(buckets: AgingBuckets): number {
  return buckets.current + buckets.d30 + buckets.d60 + buckets.d90 + buckets.older;
}

export function AgingChart({ receivables, payables, showReceivables }: AgingChartProps) {
  const baseRows = [
    ...(showReceivables
      ? [{ name: t('dashboard.financial.agingReceivables'), ...receivables, total: total(receivables) }]
      : []),
    { name: t('dashboard.financial.agingPayables'), ...payables, total: total(payables) },
  ];
  // Invisible sliver stacked after the last bucket: LabelList skips zero-width
  // segments, so anchoring the row total to a guaranteed-nonzero segment keeps
  // the total rendered at the stack end even when the last buckets are empty.
  const maxTotal = Math.max(...baseRows.map((r) => r.total), 1);
  const rows = baseRows.map((r) => ({ ...r, labelPad: maxTotal * 0.004 }));

  const config: ChartConfig = {
    current: { label: t('dashboard.financial.bucketCurrent'), color: BUCKET_COLORS.current },
    d30: { label: t('dashboard.financial.bucket30'), color: BUCKET_COLORS.d30 },
    d60: { label: t('dashboard.financial.bucket60'), color: BUCKET_COLORS.d60 },
    d90: { label: t('dashboard.financial.bucket90'), color: BUCKET_COLORS.d90 },
    older: { label: t('dashboard.financial.bucketOlder'), color: BUCKET_COLORS.older },
  };

  const height = rows.length > 1 ? 'h-[200px]' : 'h-[160px]';

  return (
    <ChartCard title={t('dashboard.financial.aging')}>
      <ChartContainer config={config} className={`${height} w-full`}>
        <BarChart data={rows} layout="vertical" margin={{ right: 96 }} barSize={28}>
          <CartesianGrid horizontal={false} strokeOpacity={0.4} />
          <XAxis type="number" tickFormatter={formatAxis} tickLine={false} axisLine={false} />
          <YAxis
            type="category"
            dataKey="name"
            tickLine={false}
            axisLine={false}
            width={96}
          />
          <ChartTooltip
            content={<ChartTooltipContent formatter={plnTooltipFormatter(config, { hideZero: true })} />}
          />
          <ChartLegend content={<ChartLegendContent />} />
          {BUCKET_KEYS.map((key) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="aging"
              fill={`var(--color-${key})`}
              stroke="var(--card)"
              strokeWidth={2}
            />
          ))}
          <Bar
            dataKey="labelPad"
            stackId="aging"
            fill="transparent"
            legendType="none"
            tooltipType="none"
          >
            <LabelList
              dataKey="total"
              position="right"
              offset={10}
              className="fill-foreground font-medium tabular-nums"
              fontSize={12}
              formatter={(value) => `${formatAxis(Number(value))} PLN`}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
